import { CommonModule, Location } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxColorsModule } from 'ngx-colors';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { FuseTranslationLoaderService } from '../../../../../@fuse/services/translation-loader.service';
import { FuseDirectivesModule } from '../../../../../@fuse/directives/directives';
import { EditShortcutDirective } from '../../../directives/edit-shortcut.directive';
import { AppRoutes } from '../../../const/AppRoutes';
import { MATERIAL_COLOR_PALETTE } from '../../../const/MaterialColorPalette';
import {
  OrganizationIdentity,
  OrganizationIdentityPayload,
} from '../../../api/models/OrganizationIdentityModel';
import { OrganizationIdentitiesService } from '../../../api/organization-identities.service';
import { AddressComponent } from '../../../components/address/address.component';
import { ContactComponent } from '../../../components/contact/contact.component';
import { SignatureRichEditorComponent } from '../../../components/signature-rich-editor/signature-rich-editor.component';
import { AssetUploadService } from '../../../services/asset-upload.service';
import { GT2PageAbstract } from '../../abstract/GT2PageAbstract';
import { locale as english } from '../crud-organization/i18n/en-CA';
import { locale as french } from '../crud-organization/i18n/fr-CA';
import { NgxPermissionsModule } from 'ngx-permissions';

export const ASSET_TYPE_IDENTITY_WHITE = 'identity_white';
export const ASSET_TYPE_IDENTITY_COLOR = 'identity_color';
export const ASSET_TYPE_PRODUCT_DEFAULT = 'product_default';

@Component({
  selector: 'app-crud-organization-identity',
  templateUrl: './crud-organization-identity.component.html',
  styleUrls: ['./crud-organization-identity.component.scss'],
  standalone: true,
  imports: [
    NgxPermissionsModule,
    CommonModule,
    FormsModule,
    FuseDirectivesModule,
    EditShortcutDirective,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    NgxColorsModule,
    AddressComponent,
    ContactComponent,
    SignatureRichEditorComponent,
  ],
})
export class CrudOrganizationIdentityComponent
  extends GT2PageAbstract
  implements OnInit, OnDestroy
{
  @ViewChild('logoWhiteInput') logoWhiteInput!: ElementRef<HTMLInputElement>;
  @ViewChild('logoColorInput') logoColorInput!: ElementRef<HTMLInputElement>;
  @ViewChild('productDefaultInput')
  productDefaultInput!: ElementRef<HTMLInputElement>;
  @ViewChild('addressComponent') addressComponent?: AddressComponent;
  @ViewChild('contactComponent') contactComponent?: ContactComponent;

  shopUUID!: string;
  identityUUID!: string;
  identity: OrganizationIdentity;
  isCreate: boolean = true;
  editMode: boolean = false;
  override loading: boolean = false;
  saving: boolean = false;
  logoWhiteUploading: boolean = false;
  logoColorUploading: boolean = false;
  productDefaultUploading: boolean = false;

  colorPalette = MATERIAL_COLOR_PALETTE;
  emailBgColor: string = '';

  readonly ASSET_TYPE_IDENTITY_WHITE = ASSET_TYPE_IDENTITY_WHITE;
  readonly ASSET_TYPE_IDENTITY_COLOR = ASSET_TYPE_IDENTITY_COLOR;

  private paletteObserver?: MutationObserver;

  constructor(
    private logger: NGXLogger,
    private toastr: ToastrService,
    public translate: TranslateService,
    private translationLoader: FuseTranslationLoaderService,
    public router: Router,
    public location: Location,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private identitiesService: OrganizationIdentitiesService,
    private assetUploadService: AssetUploadService,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    this.identity = this.emptyIdentity();
  }

  ngOnInit(): void {
    this.route.params.subscribe({
      next: (params: any) => {
        this.shopUUID = params.shop_uuid;
        this.identityUUID = params.id;
        if (this.identityUUID && this.identityUUID !== 'new') {
          this.isCreate = false;
          this.loadIdentity(this.identityUUID);
        } else {
          this.isCreate = true;
          this.identity = this.emptyIdentity();
          this.syncColors();
        }
      },
    });

    this.paletteObserver = new MutationObserver(() => {
      this.repositionColorPalette();
    });
    this.paletteObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  ngOnDestroy(): void {
    if (this.paletteObserver) {
      this.paletteObserver.disconnect();
    }
  }

  private emptyIdentity(): OrganizationIdentity {
    return {
      uuid: '',
      name: '',
      email_background_color: null,
      logo_white: null,
      logo_color: null,
      address: null,
      contact: null,
    };
  }

  /**
   * Toggle edit mode for the Address & Contact panels (mirrors the
   * Organization edit page). Those two sub-resources are saved by their own
   * components via their dedicated /v1/addresses & /v1/contacts endpoints.
   */
  public onEditAddressContact(): void {
    this.editMode = !this.editMode;
    if (this.addressComponent) {
      this.addressComponent.editMode = this.editMode;
    }
    if (this.contactComponent) {
      this.contactComponent.editMode = this.editMode;
    }
  }

  private syncColors(): void {
    this.emailBgColor = this.identity.email_background_color || '';
  }

  public loadIdentity(uuid: string): void {
    // The identity is resolved from the list endpoint (which returns the full
    // objects) rather than a per-uuid detail endpoint.
    this.loading = true;
    this.identitiesService.getAll().subscribe({
      next: (response) => {
        this.loading = false;
        const found = (response.data || []).find((item) => item.uuid === uuid);
        if (found) {
          this.identity = found;
          this.syncColors();
        } else {
          this.toastr.error(this.translate.instant('ORG.IDENTITY_NOT_FOUND'));
          this.onBack();
        }
      },
      error: (error) => {
        this.loading = false;
        this.logger.error(
          'CrudOrganizationIdentityComponent.loadIdentity() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.handleAPIError(
          error,
          this.dialog,
          this.toastr,
          this.translate.instant('GENERIC.ERRORS.GENERIC') + ' OIP1001',
          this.router,
        );
      },
    });
  }

  private repositionColorPalette(): void {
    const palette: HTMLElement | null = document.querySelector(
      'ngx-colors-panel .opened',
    );
    if (!palette) {
      return;
    }
    const swatch: HTMLElement | null =
      this.elementRef.nativeElement.querySelector(
        'ngx-colors .app-color-picker',
      );
    if (!swatch) {
      return;
    }

    const swatchRect = swatch.getBoundingClientRect();
    const paletteHeight = palette.offsetHeight;
    const viewportHeight = window.innerHeight;

    palette.style.transform = 'none';
    palette.style.position = 'fixed';
    palette.style.left = swatchRect.left + 'px';

    const spaceAbove = swatchRect.top;
    const spaceBelow = viewportHeight - swatchRect.bottom;

    if (spaceAbove >= paletteHeight) {
      palette.style.top = swatchRect.top - paletteHeight + 'px';
    } else if (spaceBelow >= paletteHeight) {
      palette.style.top = swatchRect.bottom + 'px';
    } else {
      palette.style.top = Math.max(0, viewportHeight - paletteHeight) + 'px';
    }
  }

  get canEditLogos(): boolean {
    return !this.isCreate && !!this.identity.uuid;
  }

  get formValid(): boolean {
    return !!this.identity.name && this.identity.name.trim().length > 0;
  }

  public setEmailBgColor(color: string): void {
    this.identity.email_background_color = color;
    this.emailBgColor = color;
  }

  public clearEmailBgColor(): void {
    this.identity.email_background_color = null;
    this.emailBgColor = '';
  }

  public onSave(): void {
    if (!this.formValid || this.saving) {
      return;
    }
    const payload: OrganizationIdentityPayload = {
      name: this.identity.name,
      website: this.identity.website ?? null,
      facebook_url: this.identity.facebook_url ?? null,
      twitter_url: this.identity.twitter_url ?? null,
      youtube_url: this.identity.youtube_url ?? null,
      linkedin_url: this.identity.linkedin_url ?? null,
      instagram_url: this.identity.instagram_url ?? null,
      tiktok_url: this.identity.tiktok_url ?? null,
      whatsapp_url: this.identity.whatsapp_url ?? null,
      pinterest_url: this.identity.pinterest_url ?? null,
      threads_url: this.identity.threads_url ?? null,
      telegram_url: this.identity.telegram_url ?? null,
      support_email: this.identity.support_email ?? null,
      support_tel: this.identity.support_tel ?? null,
      signature_fr_CA: this.identity.signature_fr_CA ?? null,
      signature_en_CA: this.identity.signature_en_CA ?? null,
      email_background_color: this.identity.email_background_color ?? null,
    };
    this.saving = true;

    if (this.isCreate) {
      this.identitiesService.create(payload).subscribe({
        next: (response) => {
          this.saving = false;
          this.identity = response.data;
          this.identityUUID = this.identity.uuid;
          this.isCreate = false;
          this.syncColors();
          this.toastr.success(
            this.translate.instant('ORG.IDENTITY_CREATE_SUCCESS'),
          );
          // Update the URL to the edit route so a refresh works and logo
          // upload unlocks, without triggering a full route reload.
          this.location.replaceState(
            '/' +
              this.shopUUID +
              '/' +
              AppRoutes.ORGANIZATION_IDENTITY +
              '/' +
              this.identity.uuid,
          );
        },
        error: (error) => {
          this.saving = false;
          this.logger.error(
            'CrudOrganizationIdentityComponent.onSave() create -> ERROR: ' +
              JSON.stringify(error),
          );
          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('GENERIC.ERRORS.GENERIC') + ' OIP2001',
            this.router,
          );
        },
      });
    } else {
      this.identitiesService.update(this.identity.uuid, payload).subscribe({
        next: (response) => {
          this.saving = false;
          this.identity = response.data;
          this.syncColors();
          this.toastr.success(
            this.translate.instant('ORG.IDENTITY_UPDATE_SUCCESS'),
          );
        },
        error: (error) => {
          this.saving = false;
          this.logger.error(
            'CrudOrganizationIdentityComponent.onSave() update -> ERROR: ' +
              JSON.stringify(error),
          );
          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('GENERIC.ERRORS.GENERIC') + ' OIP2002',
            this.router,
          );
        },
      });
    }
  }

  public onBack(): void {
    this.onPageBack(this.location, this.router);
  }

  public onSelectLogoFile(assetType: string): void {
    if (!this.canEditLogos) {
      return;
    }
    const input =
      assetType === ASSET_TYPE_IDENTITY_WHITE
        ? this.logoWhiteInput
        : this.logoColorInput;
    input.nativeElement.click();
  }

  public onLogoFileSelected(event: Event, assetType: string): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];

    if (file.type !== 'image/png') {
      this.toastr.error(this.translate.instant('ORG.LOGO_INVALID_FORMAT'));
      input.value = '';
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width !== 272 || img.height !== 72) {
        this.toastr.error(
          this.translate.instant('ORG.LOGO_INVALID_DIMENSIONS'),
        );
        input.value = '';
        return;
      }
      this.uploadLogo(file, assetType);
      input.value = '';
    };
    img.onerror = () => {
      this.toastr.error(this.translate.instant('ORG.LOGO_INVALID_FORMAT'));
      input.value = '';
    };
    img.src = URL.createObjectURL(file);
  }

  private uploadLogo(file: File, assetType: string): void {
    const isWhite = assetType === ASSET_TYPE_IDENTITY_WHITE;
    if (isWhite) {
      this.logoWhiteUploading = true;
    } else {
      this.logoColorUploading = true;
    }

    this.assetUploadService
      .uploadAsset(file, assetType, this.identity.uuid)
      .subscribe({
        next: (response: any) => {
          const url = response.url + '?t=' + Date.now();
          if (isWhite) {
            this.identity.logo_white = url;
            this.logoWhiteUploading = false;
          } else {
            this.identity.logo_color = url;
            this.logoColorUploading = false;
          }
          this.toastr.success(
            this.translate.instant('ORG.UPLOAD_LOGO_TYPE_SUCCESS'),
          );
        },
        error: (error: any) => {
          if (isWhite) {
            this.logoWhiteUploading = false;
          } else {
            this.logoColorUploading = false;
          }
          this.logger.error(
            'CrudOrganizationIdentityComponent.uploadLogo() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.toastr.error(
            this.translate.instant('ORG.UPLOAD_LOGO_TYPE_ERROR'),
          );
        },
      });
  }

  public onSelectProductImage(): void {
    if (!this.canEditLogos) {
      return;
    }
    this.productDefaultInput.nativeElement.click();
  }

  public onProductImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];

    if (file.type !== 'image/png') {
      this.toastr.error(this.translate.instant('ORG.LOGO_INVALID_FORMAT'));
      input.value = '';
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width !== 250 || img.height !== 250) {
        this.toastr.error(
          this.translate.instant('ORG.PRODUCT_DEFAULT_INVALID_DIMENSIONS'),
        );
        input.value = '';
        return;
      }
      this.uploadProductImage(file);
      input.value = '';
    };
    img.onerror = () => {
      this.toastr.error(this.translate.instant('ORG.LOGO_INVALID_FORMAT'));
      input.value = '';
    };
    img.src = URL.createObjectURL(file);
  }

  private uploadProductImage(file: File): void {
    this.productDefaultUploading = true;
    // asset_type=product_default + entity_uuid of the identity -> the backend
    // writes shop_product_default_{identityId}.png for this identity.
    this.assetUploadService
      .uploadAsset(file, ASSET_TYPE_PRODUCT_DEFAULT, this.identity.uuid)
      .subscribe({
        next: (response: any) => {
          this.identity.product_default_image =
            response.url + '?t=' + Date.now();
          this.productDefaultUploading = false;
          this.toastr.success(
            this.translate.instant('ORG.PRODUCT_DEFAULT_UPLOAD_SUCCESS'),
          );
        },
        error: (error: any) => {
          this.productDefaultUploading = false;
          this.logger.error(
            'CrudOrganizationIdentityComponent.uploadProductImage() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.toastr.error(
            this.translate.instant('ORG.PRODUCT_DEFAULT_UPLOAD_ERROR'),
          );
        },
      });
  }
}
